import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";
import { ErrorsCodes, UserCulture } from "../../libs/enums";
import { ChangePasswordModel } from "../../libs/models/change-password";
import { Response } from "../../libs/models/generic-response";
import {
  ClaimType,
  LoggedUserStatus,
  LoginModel,
  LoginResponse,
  LoginTermsConditionsResponse,
  RolesTypes,
} from "../../libs/models/login";
import { ValidationModel } from "../../libs/models/validate-model";
import { CROSSPAY_ROUTES } from "../../libs/routes";
import { validateChangePasswordState } from "../../pages/ChangePassword/change-password.policy";
import { validateState } from "../../pages/Signin/login-form-container.policy";
import { changePassword, loginService } from "../../services/crosspay-service";
import { AsyncAppThunk } from "../application-state";
import { CustomizedBack, CustomizedPush } from "store/navigation-wrapper";
import { addSnack, showLoaderWhile } from "./ui";
import translate from "utils/translations";

export interface LoginStore {
  email: string;
  password: string;
  ErrorCode?: ErrorsCodes;
  isValidating: boolean;
  userCulture: UserCulture;
  loggedUser: LoginResponse | null;
  userRole?: RolesTypes;
  loggedUserRegistrationStatus: LoggedUserStatus | null;
  isTermsConditionsDialogOpen: boolean;
  isPasswordExpired: boolean;
  termsConditions: LoginTermsConditionsResponse | null;
  changePasswordModel: ChangePasswordModel;
  isChangePasswordValidating: boolean;
}

const initialState: LoginStore = {
  isValidating: false,
  email: "",
  password: "",
  userCulture: UserCulture.English,
  loggedUser: null,
  loggedUserRegistrationStatus: null,
  isTermsConditionsDialogOpen: false,
  termsConditions: null,
  isPasswordExpired: false,
  changePasswordModel: {
    oldPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  },
  isChangePasswordValidating: false,
};

const slice = createSlice({
  name: "login",
  initialState,
  reducers: {
    setEmail: (state: LoginStore, action: PayloadAction<string>) => {
      state.email = action.payload;
      state.ErrorCode && (state.ErrorCode = undefined);
    },
    setPassword: (state: LoginStore, action: PayloadAction<string>) => {
      state.password = action.payload;
      state.ErrorCode && (state.ErrorCode = undefined);
    },
    setErrorCode: (
      state: LoginStore,
      action: PayloadAction<ErrorsCodes | undefined>
    ) => {
      state.ErrorCode = action.payload;
    },
    setIsValidating: (state: LoginStore, action: PayloadAction<boolean>) => {
      state.isValidating = action.payload;
    },
    setLoggedUser: (
      state: LoginStore,
      action: PayloadAction<LoginResponse>
    ) => {
      state.loggedUser = action.payload;
    },
    setUserRole: (state: LoginStore, action: PayloadAction<RolesTypes>) => {
      state.userRole = action.payload;
    },
    setLoggedUserStatus: (
      state: LoginStore,
      action: PayloadAction<LoggedUserStatus | null>
    ) => {
      state.loggedUserRegistrationStatus = action.payload;
    },
    setTermsConditions: (
      state: LoginStore,
      action: PayloadAction<LoginTermsConditionsResponse | null>
    ) => {
      state.termsConditions = action.payload;
    },
    setIsTermsConditionsDialogOpen: (
      state: LoginStore,
      action: PayloadAction<boolean>
    ) => {
      state.isTermsConditionsDialogOpen = action.payload;
    },
    setIsPasswordExpired: (
      state: LoginStore,
      action: PayloadAction<boolean>
    ) => {
      state.isPasswordExpired = action.payload;
    },
    clearLoginData: () => {
      return initialState;
    },
    setChangePasswordModelProps: (
      state: LoginStore,
      action: PayloadAction<{ key: string; value: any }>
    ) => {
      state.changePasswordModel = {
        ...state.changePasswordModel,
        [action.payload.key]: action.payload.value,
      };
    },
    setIsChangePasswordValidating: (
      state: LoginStore,
      action: PayloadAction<boolean>
    ) => {
      state.isChangePasswordValidating = action.payload;
    },
    clearChangePasswordModel: (state: LoginStore) => {
      state.changePasswordModel = initialState.changePasswordModel;
      state.isChangePasswordValidating = false;
    },
  },
});

export const {
  setEmail,
  setPassword,
  setErrorCode,
  setLoggedUser,
  setIsValidating,
  setUserRole,
  setLoggedUserStatus,
  setTermsConditions,
  setIsTermsConditionsDialogOpen,
  clearLoginData,
  setIsPasswordExpired,
  setChangePasswordModelProps,
  clearChangePasswordModel,
  setIsChangePasswordValidating,
} = slice.actions;
export const reducer = slice.reducer;

export const setLoginLogic = createAsyncThunk(
  "login/setLoginLogic",
  async (loginResponse: Response<LoginResponse> | null, { dispatch }) => {
    if (loginResponse && loginResponse.result != null) {
      let token: any = jwtDecode(loginResponse.result.token);
      dispatch(setLoggedUser(loginResponse.result));
      dispatch(setUserRole(token[ClaimType.Role]));
      dispatch(setIsValidating(false));
      dispatch(setIsPasswordExpired(loginResponse.result.isPasswordExpired));
    }
  }
);

export const login = (): AsyncAppThunk => async (dispatch, store) => {
  dispatch(setIsValidating(true));

  var loginState = store().login;
  var validationState = validateState(loginState);
  if (Object.keys(validationState).length < 1) {
    const loginModel: LoginModel = {
      userName: loginState.email,
      password: loginState.password,
    };

    const loginResponse = await loginService(loginModel);
    dispatch(setLoginLogic(loginResponse));
  }
};

export const onPasswordChangeSubmit =
  (passwordModel: ChangePasswordModel): AsyncAppThunk =>
  async (dispatch, store) => {
    dispatch(setIsChangePasswordValidating(true));
    if (Object.keys(validateChangePasswordState(store().login)).length === 0) {
      dispatch(
        showLoaderWhile(async () => {
          dispatch(setIsChangePasswordValidating(false));
          const response = await changePassword(passwordModel);
          if (
            response &&
            Object.keys(response).length > 0 &&
            response.result !== null
          ) {
            CustomizedBack();
            dispatch(
              addSnack({
                message: translate(
                  "Password_Changed_Text",
                  "Password changed successfully"
                ),
              })
            );
          }
        })
      );
    }
  };

export const navigateUserToViewPortal =
  (): AsyncAppThunk => async (dispatch, store) => {
    const userRole = store().login.userRole;
    if (userRole !== null && userRole !== RolesTypes.POSUser) {
      CustomizedPush(CROSSPAY_ROUTES.PRODUCT);
    } else {
      CustomizedPush(CROSSPAY_ROUTES.CARD_OPERATIONS);
    }
  };
